/**
 * Сервис для кеширования видео креативов с использованием Cache API
 * Адаптирован из OpenAIImage для MarketingTool
 */

const CACHE_NAME = 'creatives-video-cache-v1';
const CACHE_EXPIRY_DAYS = 30; // Кеш на 30 дней
const MAX_CACHE_SIZE_MB = 10240; // Максимум 10 ГБ

class VideoCacheService {
  constructor() {
    this.initPromise = this.init();
  }

  /**
   * Инициализация сервиса
   */
  async init() {
    try {
      // Проверяем поддержку Cache API
      if (!('caches' in window)) {
        console.warn('[VideoCache] Cache API не поддерживается');
        return;
      }

      console.log('[VideoCache] Сервис инициализирован');
    } catch (error) {
      console.error('[VideoCache] Ошибка инициализации:', error);
    }
  }

  /**
   * Получить видео из кеша
   */
  async get(url) {
    try {
      await this.initPromise;

      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(url);

      if (response) {
        console.log(`[VideoCache] ✅ Видео найдено в кеше: ${url}`);
        return await response.blob();
      }

      return null;
    } catch (error) {
      console.error('[VideoCache] Ошибка получения из кеша:', error);
      return null;
    }
  }

  /**
   * Сохранить видео в кеш
   */
  async set(url, blob) {
    try {
      await this.initPromise;

      const cache = await caches.open(CACHE_NAME);
      const response = new Response(blob, {
        headers: {
          'Content-Type': blob.type || 'video/mp4',
          'Cache-Control': `private, max-age=${CACHE_EXPIRY_DAYS * 24 * 60 * 60}`,
          'X-Cached-At': Date.now().toString()
        }
      });
      
      await cache.put(url, response);
      
      console.log(`[VideoCache] 💾 Видео сохранено: ${url} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
      
      // Проверяем размер кеша и очищаем если нужно
      await this.cleanupIfNeeded();
    } catch (error) {
      console.error('[VideoCache] Ошибка сохранения в кеш:', error);
    }
  }

  /**
   * Сохранить превью видео
   */
  async saveThumbnail(videoUrl, thumbnailBlob) {
    try {
      const thumbnailUrl = `${videoUrl}_thumbnail`;
      const cache = await caches.open(CACHE_NAME);
      
      const response = new Response(thumbnailBlob, {
        headers: {
          'Content-Type': 'image/jpeg',
          'Cache-Control': `private, max-age=${CACHE_EXPIRY_DAYS * 24 * 60 * 60}`,
          'X-Cached-At': Date.now().toString()
        }
      });
      
      await cache.put(thumbnailUrl, response);
      
      console.log(`[VideoCache] 🖼️ Превью сохранено для: ${videoUrl}`);
    } catch (error) {
      console.error('[VideoCache] Ошибка сохранения превью:', error);
    }
  }

  /**
   * Получить превью видео
   */
  async getThumbnail(videoUrl) {
    try {
      const thumbnailUrl = `${videoUrl}_thumbnail`;
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(thumbnailUrl);
      
      if (response) {
        return await response.blob();
      }
      
      return null;
    } catch (error) {
      console.error('[VideoCache] Ошибка получения превью:', error);
      return null;
    }
  }

  /**
   * Генерация thumbnail из видео
   */
  async generateThumbnail(videoBlob, timeInSeconds = 0) {
    return new Promise((resolve, reject) => {
      try {
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        
        const blobUrl = URL.createObjectURL(videoBlob);
        video.src = blobUrl;
        
        video.onloadedmetadata = () => {
          // Устанавливаем размер canvas
          canvas.width = 200;
          canvas.height = (video.videoHeight / video.videoWidth) * 200;
          
          video.currentTime = timeInSeconds;
        };
        
        video.onseeked = () => {
          try {
            // Рисуем кадр на canvas
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            // Конвертируем в blob
            canvas.toBlob((blob) => {
              URL.revokeObjectURL(blobUrl);
              resolve(blob);
            }, 'image/jpeg', 0.8);
          } catch (error) {
            URL.revokeObjectURL(blobUrl);
            reject(error);
          }
        };
        
        video.onerror = (error) => {
          URL.revokeObjectURL(blobUrl);
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Получить статистику кеша
   */
  async getStats() {
    try {
      const cache = await caches.open(CACHE_NAME);
      const keys = await cache.keys();
      
      let totalSize = 0;
      let videoCount = 0;
      let thumbnailCount = 0;
      
      for (const request of keys) {
        const response = await cache.match(request);
        if (response) {
          const blob = await response.blob();
          totalSize += blob.size;
          
          if (request.url.includes('_thumbnail')) {
            thumbnailCount++;
          } else {
            videoCount++;
          }
        }
      }
      
      return {
        videoCount,
        thumbnailCount,
        totalSize,
        totalSizeMB: (totalSize / 1024 / 1024).toFixed(2)
      };
    } catch (error) {
      console.error('[VideoCache] Ошибка получения статистики:', error);
      return { videoCount: 0, thumbnailCount: 0, totalSize: 0, totalSizeMB: '0.00' };
    }
  }

  /**
   * Очистка кеша если превышен лимит (LRU)
   */
  async cleanupIfNeeded() {
    try {
      const stats = await this.getStats();
      const currentSizeMB = parseFloat(stats.totalSizeMB);
      
      if (currentSizeMB > MAX_CACHE_SIZE_MB) {
        console.log(`[VideoCache] 🧹 Превышен лимит кеша (${currentSizeMB} MB > ${MAX_CACHE_SIZE_MB} MB), начинаем очистку...`);
        
        const cache = await caches.open(CACHE_NAME);
        const keys = await cache.keys();
        
        // Получаем все записи с временем кеширования
        const entries = [];
        for (const request of keys) {
          const response = await cache.match(request);
          if (response) {
            const cachedAt = response.headers.get('X-Cached-At');
            entries.push({
              request,
              cachedAt: cachedAt ? parseInt(cachedAt) : 0,
              size: (await response.blob()).size
            });
          }
        }
        
        // Сортируем по времени (старые первыми)
        entries.sort((a, b) => a.cachedAt - b.cachedAt);
        
        // Удаляем старые записи пока не достигнем 80% от лимита
        const targetSize = MAX_CACHE_SIZE_MB * 0.8 * 1024 * 1024;
        let currentSize = stats.totalSize;
        let deletedCount = 0;
        
        for (const entry of entries) {
          if (currentSize <= targetSize) break;
          
          await cache.delete(entry.request);
          currentSize -= entry.size;
          deletedCount++;
        }
        
        console.log(`[VideoCache] ✅ Удалено ${deletedCount} старых записей`);
      }
    } catch (error) {
      console.error('[VideoCache] Ошибка очистки кеша:', error);
    }
  }

  /**
   * Удалить видео из кеша
   */
  async delete(url) {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.delete(url);
      await cache.delete(`${url}_thumbnail`);
      
      console.log(`[VideoCache] 🗑️ Видео удалено из кеша: ${url}`);
    } catch (error) {
      console.error('[VideoCache] Ошибка удаления из кеша:', error);
    }
  }

  /**
   * Очистить весь кеш
   */
  async clear() {
    try {
      await caches.delete(CACHE_NAME);
      console.log('[VideoCache] 🗑️ Кеш полностью очищен');
    } catch (error) {
      console.error('[VideoCache] Ошибка очистки кеша:', error);
    }
  }
}

// Создаем единственный экземпляр сервиса
export const videoCacheService = new VideoCacheService();

// Экспортируем для отладки
if (typeof window !== 'undefined') {
  window.videoCacheService = videoCacheService;
}
